/** Closed list of behavioural event types the SDK is allowed to emit (M2, M3, technical §16.6–16.7). */
export const EVENT_TYPES = [
  'page_view',
  'step_enter',
  'step_exit',
  'field_focus',
  'field_blur',
  'field_change',
  'field_error',
  'hesitation',
  'rage_click',
  'dead_click',
  'submit',
  'journey_exit',
  'guidance_shown',
  'guidance_opened',
  'guidance_dismissed',
  'guidance_step_reached',
  'guidance_completed',
  'help_requested',
  'anchor_drift',
  'anchor_failed',
  'sdk_error',
  'feedback_prompt_shown',
  'feedback_prompt_dismissed',
  'feedback_given',
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

/**
 * Whitelist of payload keys per event type (technical §16.4, rule 1). A key
 * absent from this list is rejected by the schema and dropped by the SDK's
 * anonymizer; free text never enters the payload.
 */
export const EVENT_PAYLOAD_KEYS: Readonly<Record<EventType, readonly string[]>> = {
  page_view: [],
  step_enter: [],
  step_exit: ['durationMs'],
  field_focus: [],
  field_blur: ['durationMs'],
  field_change: ['filled'],
  field_error: ['category'],
  hesitation: ['durationMs'],
  rage_click: ['count'],
  dead_click: [],
  submit: [],
  journey_exit: [],
  guidance_shown: ['guidanceId', 'component'],
  guidance_opened: ['guidanceId', 'component'],
  guidance_dismissed: ['guidanceId', 'component'],
  guidance_step_reached: ['guidanceId', 'component', 'stepIndex', 'stepCount'],
  guidance_completed: ['guidanceId', 'component'],
  help_requested: [],
  anchor_drift: ['guidanceId', 'strategy', 'failedCount'],
  anchor_failed: ['guidanceId', 'failedCount'],
  sdk_error: ['code', 'subsystem'],
  feedback_prompt_shown: ['promptId', 'kind'],
  feedback_prompt_dismissed: ['promptId', 'kind'],
  /**
   * The answer of the visitor (module M4): closed keys only. Stored apart from
   * the behavioural events, in `telemetry.feedback_entries` (technical §13.4).
   */
  feedback_given: ['promptId', 'kind', 'answer', 'announcementKey', 'announcementType', 'entityKey'],
};

/** Payload keys of `feedback_given` that must be present for the feedback to be stored. */
export const FEEDBACK_REQUIRED_KEYS = ['promptId', 'kind', 'answer', 'announcementKey'] as const;
/** Payload keys of `feedback_given` the page may leave out (no declared marker). */
export const FEEDBACK_OPTIONAL_KEYS = ['announcementType', 'entityKey'] as const;

/** Closed categories of a field error (functional §6.6). Never a message, never a value. */
export const FIELD_ERROR_CATEGORIES = [
  'invalid_format',
  'required_missing',
  'out_of_range',
  'duplicate',
  'other',
] as const;
export type FieldErrorCategory = (typeof FIELD_ERROR_CATEGORIES)[number];

/** Anchoring strategies, in the order the SDK applies them (technical §16.6). */
export const ANCHOR_STRATEGIES = [
  'stable_identifier',
  'label_semantic',
  'role_and_position',
  'ordinal_in_list',
] as const;
export type AnchorStrategy = (typeof ANCHOR_STRATEGIES)[number];

/** SDK subsystems that may report an internal error (technical §16.2, §16.7). */
export const SDK_SUBSYSTEMS = ['core', 'capture', 'guidance', 'triggering', 'feedback', 'transport'] as const;
export type SdkSubsystem = (typeof SDK_SUBSYSTEMS)[number];
